import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, money } from "../lib/api";
import { toast } from "sonner";
import { ChatCircleText, PaperPlaneTilt, X } from "@phosphor-icons/react";
import { useMessageTemplates, renderMessage } from "../lib/messageTemplates";

const API_KEYS = {
  parts_ordered: "ticket_parts_ordered", parts_arrived: "ticket_parts_arrived", diagnosis_ready: "ticket_diagnosis_ready",
  ready_for_pickup: "ticket_ready_pickup", overdue_pickup: "ticket_overdue_pickup",
};

/**
 * SendTicketSmsModal
 * ------------------
 * Fires a pre-templated SMS update to the ticket's customer.  Ships 5 common
 * shop-floor scenarios (parts ordered, parts arrived, diagnosis ready, ready
 * for pickup, overdue pickup) plus a "Custom" option so techs can type their
 * own text.  All templates support `{tokens}` that are resolved from the
 * ticket + store context passed in via props — no backend string-interpolation,
 * keeps the audit log exactly matching what the customer received.
 */

const TEMPLATES = [
  {
    key: "parts_ordered",
    label: "Part(s) ordered",
    tone: "#C98B24",
    body:
      "Hi {customer}, just a quick update — your {device} needs a part that we've now ordered. " +
      "We'll get back to you the moment it arrives. Ticket {ticket}. — {store}",
  },
  {
    key: "parts_arrived",
    label: "Part has arrived",
    tone: "#2F8F6E",
    body:
      "Hi {customer}, good news — the part for your {device} has arrived and we've started the repair. " +
      "Ticket {ticket}. — {store}",
  },
  {
    key: "diagnosis_ready",
    label: "Diagnosis ready",
    tone: "#1E5FEB",
    body:
      "Hi {customer}, we've finished diagnosing your {device}. " +
      "Please give us a call on {store_phone} when you have a minute so we can go through the options. Ticket {ticket}. — {store}",
  },
  {
    key: "ready_for_pickup",
    label: "Ready for pickup",
    tone: "#2F8F6E",
    body:
      "Hi {customer}, your {device} is repaired and ready for pickup at {store}. " +
      "Balance due on collection: ${balance}. Ticket {ticket}.",
  },
  {
    key: "overdue_pickup",
    label: "Pickup reminder",
    tone: "#DC2626",
    body:
      "Hi {customer}, friendly reminder that your {device} has been repaired and is waiting for pickup at {store}. " +
      "Please collect at your earliest convenience. Ticket {ticket}.",
  },
  {
    key: "custom",
    label: "Custom message",
    tone: "#6B7280",
    body: "",
  },
];

function renderTemplate(body, ctx) {
  if (!body) return "";
  return body
    .replace(/\{customer\}/g, ctx.customer || "there")
    .replace(/\{device\}/g, `${ctx.device_make || ""} ${ctx.device_model || ""}`.trim() || "device")
    .replace(/\{ticket\}/g, ctx.ticket_number || "")
    .replace(/\{store\}/g, ctx.store_name || "iRepair")
    .replace(/\{store_phone\}/g, ctx.store_phone || "the shop")
    .replace(/\{balance\}/g, money(ctx.balance || 0));
}

const SMS_SEGMENT = 160;

export default function SendTicketSmsModal({ open, onClose, ticket, store, onSent }) {
  const [selected, setSelected] = useState("parts_ordered");
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [to, setTo] = useState("");
  const tplMap = useMessageTemplates();
  const textFor = (key, c) => {
    const saved = tplMap?.[API_KEYS[key]]?.sms;
    if (saved) {
      return renderMessage(saved, {
        customer: c.customer || "there", device: `${c.device_make} ${c.device_model}`.trim() || "device",
        ticket: c.ticket_number, store: c.store_name, store_phone: c.store_phone, balance: money(c.balance || 0),
      });
    }
    return renderTemplate(TEMPLATES.find((t) => t.key === key)?.body, c);
  };

  const ctx = useMemo(() => ({
    customer: (ticket?.customer_name || "").split(" ")[0] || "",
    device_make: ticket?.device_make || "",
    device_model: ticket?.device_model || "",
    ticket_number: ticket?.ticket_number || "",
    store_name: store?.name || "the shop",
    store_phone: store?.phone || "",
    // Prefer final_cost (after completion) → fall back to estimated_cost
    balance: Number(ticket?.final_cost || ticket?.estimated_cost || 0),
  }), [ticket, store]);

  // Reset form each time we open
  useEffect(() => {
    if (!open) return;
    setSelected("parts_ordered");
    setBody(textFor("parts_ordered", ctx));
    setTo(ticket?.customer_phone || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ctx, ticket?.customer_phone, tplMap]);

  const selectTemplate = (key) => {
    setSelected(key);
    const tpl = TEMPLATES.find((t) => t.key === key);
    if (tpl.key === "custom") {
      setBody("");
    } else {
      setBody(textFor(key, ctx));
    }
  };

  const send = async () => {
    const text = body.trim();
    if (!text) return toast.error("Message is empty");
    if (!to.trim()) return toast.error("No phone number on this ticket");
    setSending(true);
    try {
      await api.post(`/repair-tickets/${ticket.id}/sms`, {
        message: text,
        to: to.trim(),
        template_key: selected,
      });
      toast.success(`SMS sent to ${to.trim()}`);
      onSent?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't send SMS");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  const charCount = body.length;
  const segmentCount = Math.max(1, Math.ceil(charCount / SMS_SEGMENT));

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-950/55 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !sending && onClose()}
      data-testid="ticket-sms-modal"
    >
      <div
        className="bg-white w-full max-w-xl rounded-[24px] shadow-2xl border border-slate-200/60 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-slate-100 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold flex items-center gap-1.5">
              <ChatCircleText size={12} weight="bold" /> SEND CUSTOMER SMS
            </div>
            <h3 className="font-heading font-bold text-[18px] text-slate-900 leading-tight mt-1">
              {ticket?.customer_name || "Walk-in"}
              <span className="text-slate-400 font-mono font-normal text-[13px] ml-2">
                · {ticket?.ticket_number}
              </span>
            </h3>
            <div className="text-[12px] text-slate-500 mt-0.5 truncate">
              {ticket?.device_make} {ticket?.device_model}
            </div>
          </div>
          <button
            onClick={() => !sending && onClose()}
            data-testid="ticket-sms-close"
            className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 shrink-0"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Template chips */}
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-2">Template</div>
            <div className="flex flex-wrap gap-1.5">
              {TEMPLATES.map((t) => {
                const active = selected === t.key;
                return (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => selectTemplate(t.key)}
                    data-testid={`sms-template-${t.key}`}
                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-medium transition-all border ${
                      active
                        ? "bg-[#121216] text-white border-[#121216] shadow-[0_2px_8px_rgba(17,17,17,0.18)]"
                        : "bg-white text-slate-600 border-slate-200 hover:border-[#121216] hover:text-slate-900"
                    }`}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: active ? "#FFFFFF" : t.tone }}
                    />
                    {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* To */}
          <label className="block">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1.5">To</div>
            <input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              data-testid="ticket-sms-to"
              placeholder="+61 400 000 000"
              className="w-full h-11 px-3 rounded-xl border border-slate-200 focus:border-[#1E5FEB] focus:ring-2 focus:ring-blue-100 focus:outline-none text-sm font-mono"
            />
          </label>

          {/* Message body */}
          <label className="block">
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Message</div>
              <div className="text-[10px] text-slate-400 font-mono tabular-nums" data-testid="sms-char-count">
                {charCount} chars · {segmentCount} SMS segment{segmentCount !== 1 ? "s" : ""}
              </div>
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              data-testid="ticket-sms-body"
              rows={5}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-[#1E5FEB] focus:ring-2 focus:ring-blue-100 focus:outline-none text-sm leading-relaxed resize-none"
              placeholder="Type your message…"
            />
            <div className="text-[11px] text-slate-400 mt-1.5 italic">
              Variables like {"{customer}"} and {"{ticket}"} resolve automatically from this ticket.
            </div>
          </label>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="h-11 px-4 rounded-xl border border-slate-200 hover:border-slate-400 text-[13px] font-semibold text-slate-600 disabled:opacity-50"
          >
            CANCEL
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending || !body.trim() || !to.trim()}
            data-testid="ticket-sms-send"
            className="h-11 px-5 rounded-xl bg-gradient-to-r from-[#1E5FEB] to-[#1849C8] hover:from-[#1849C8] hover:to-[#123a9e] disabled:from-slate-300 disabled:to-slate-400 text-white text-[13px] font-semibold flex items-center gap-2 shadow-md shadow-blue-500/25"
          >
            <PaperPlaneTilt size={13} weight="bold" />
            {sending ? "SENDING…" : "SEND SMS"}
          </button>
        </div>
      </div>
    </div>
  );
}
